import { BNGoogleTagManager, EventAction, EventLabel } from "../../Helpers/GoogleTagManager/BNGoogleTagManager";
import { ArrowBack, Person as PersonIcon } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router";

import BNLogo from "../BNLogo/BNLogo";
import { Box, Button, Link as MuiLink } from "@mui/material";
import HeaderButton from "./HeaderButton";
import Notifications from "./Notifications";
import AccountSettingsWrapper from "./AccountSettings";
import getRouteAction from "../../Helpers/dataUitAttributes/dataUitAttributes";
import BNButton from "../FormElements/BNButton/BNButton";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import BNBox from "../BNBox/BNBox";
//import { PublicPortalDataLayerEvent } from "../../Helpers/GoogleTagManager/events/PublicPortalDataLayerEvent";
import LevelChangeAnimation from "./LevelChangeAnimation";
import { ReactNode, useContext, useEffect } from "react";
import useStatePersisted from "../../Helpers/useStatePersisted/useStatePersisted";
import { UiElementClickEvent } from "../../Helpers/GoogleTagManager/UiElementClickEvent";
import { BNThemeMode } from "../../bnMuiTheme";

export interface UserNavigationLinks {
  help: NavigationLink;
  settings: NavigationLink;
  mailbox: NavigationLink;
  logout: NavigationLink;
  home: NavigationLink;
  creditCard: NavigationLink;
  loan: NavigationLink;
  addOns: NavigationLink;
  savings: NavigationLink;
  privacyPolicy: NavigationLink;
  cookiePolicy: NavigationLink;
}

export interface BNHeaderProps {
  isLoggedIn: boolean;
  homeLink: NavigationLink;
  loginLink?: string;
  levelUpLink?: NavigationLink;
  level?: number;
  hideButtons?: boolean;
  notificationText?: string;
  accountText?: string;
  altLinkText: string;
  otherLanguage?: OtherLanguage;
  userNavigationLinks?: UserNavigationLinks;
  settingsLinks?: Array<NavigationLink>;
  messageList: Array<Message>;
  strings: BNHeaderStrings;
  isSpa: (path: string) => boolean;
  headerDesktopPaddingX?: 1 | 4;
  headerMaxWidth?: string;
  isApp: boolean;
  transparentBg?: boolean;
  isSticky?: boolean;
  children?: ReactNode;
}

export interface NavigationLink {
  title: string;
  url: string;
  hiddenOnMobile?: boolean;
  hiddenOnDesktop?: boolean;
  svgIconSrc?: string;
  routeName?: string;
}

export interface OtherLanguage {
  url: string;
  label: string;
  languageCode: string;
}

interface Message {
  link: string;
  imageSrc: string;
  text: string;
  type: number;
}

export interface BNHeaderStrings {
  Notification: string;
  Account: string;
  BNLogoAltTextLoggedIn: string;
  loginLabel?: string;
  darkModePrimaryText: string;
  darkModeSecondaryText: string;
}

export const BNHeader: React.FC<BNHeaderProps> = (props) => {
  const theme = useTheme();
  const { mode } = useContext(BNThemeMode);
  const {
    isLoggedIn,
    strings: stringsFromProps,
    messageList,
    userNavigationLinks: userNavigationLinksProps,
    settingsLinks: settingsLinksProps,
    levelUpLink,
    level = 0,
    homeLink,
    altLinkText,
    loginLink,
    isSpa,
    headerDesktopPaddingX = 4,
    headerMaxWidth = "140rem",
    hideButtons,
    otherLanguage,
    isApp,
    transparentBg,
    accountText,
    notificationText,
    isSticky = true,
    children,
  } = props;
  const [userNavigationLinks, setUserNavigationLinks] = useStatePersisted<UserNavigationLinks | undefined>(
    null,
    "BN_HEADER_USER_NAVIGATION_LINKS",
    userNavigationLinksProps,
    false,
    "object",
  );
  useEffect(() => {
    if (userNavigationLinksProps) {
      setUserNavigationLinks(userNavigationLinksProps);
    }
  }, [userNavigationLinksProps]);

  const [settingsLinks, setSettingsLinks] = useStatePersisted<NavigationLink[] | undefined>(
    null,
    "BN_HEADER_SETTINGS_LINKS",
    settingsLinksProps,
    false,
    "object",
  );
  useEffect(() => {
    if (settingsLinksProps) {
      setSettingsLinks(settingsLinksProps);
    }
  }, [settingsLinksProps]);

  const [strings, setStrings] = useStatePersisted<BNHeaderStrings | undefined>(
    null,
    "BN_HEADER_STRINGS",
    stringsFromProps,
    false,
    "object",
  );
  useEffect(() => {
    if (stringsFromProps.Account) {
      setStrings(stringsFromProps);
    }
  }, [stringsFromProps]);
  const appLook = isApp;
  const navigation = useNavigate();
  const stickySx = isSticky
    ? {
        position: "sticky",
        top: "0",
        zIndex: 2,
        ...theme.applyStyles("dark", {
          background: "linear-gradient(to bottom, rgba(32, 36, 39, 0.85) 90%, rgba(32, 36, 39, 0.75))",
          [theme.breakpoints.up("md")]: {
            background: "linear-gradient(to bottom, rgba(32, 36, 39, 0.85) 85%, rgba(32, 36, 39, 0.75))",
          },
          backdropFilter: "blur(2rem)",
          WebkitBackdropFilter: "blur(2rem)",
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "3rem",
            transform: "translateY(100%)",
            background: "linear-gradient(to bottom, rgba(32, 36, 39, 0.5), transparent)",
            pointerEvents: "none",
          },
        }),
      }
    : {};
  const publicHomeUrl = otherLanguage?.languageCode === "fi" ? "/sv" : "/";
  const logoLink = isLoggedIn ? homeLink.url : publicHomeUrl;
  const sxLogoLink: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    height: "100%",
  };
  const sxLogo: SxProps<Theme> = { maxHeight: "100%" };
  const iconButtonSx: SxProps<Theme> = {};
  const RenderedNotificationsButton = (
    <Notifications
      key="notifications"
      text={strings?.Notification || notificationText || ""}
      messageList={messageList}
      onNavigate={(title) => {
        BNGoogleTagManager.addEventToDataLayer(
          new UiElementClickEvent(EventAction.Header, EventLabel.Alert + ":" + title),
        );
      }}
      isSpa={isSpa}
      openMenuComponentSx={iconButtonSx}
    />
  );
  const isLogoLevel = level < 2 || !levelUpLink?.title;

  return (
    <BNBox component={"header"} sx={stickySx}>
      <Box
        sx={{
          backgroundColor:
            appLook && mode !== "dark"
              ? theme.palette.background.default
              : appLook || transparentBg
                ? "transparent"
                : mode === "dark"
                  ? "transparent"
                  : "tertiary.dark",
          borderBottom: transparentBg && !appLook ? `0.1rem solid ${theme.vars.palette.divider}` : "",
          px: { xs: 2, md: 0 },
          "@media print": {
            backgroundColor: "transparent",
          },
        }}
        data-uit-container={"header"}
      >
        <BNBox
          sx={{
            display: "grid",
            gridTemplateColumns: isLoggedIn ? "2fr 1fr" : "3fr 1fr",
            gap: 1,
            alignItems: "center",
            minHeight: "6rem",
            maxWidth: headerMaxWidth,
            mx: "auto",
            [theme.breakpoints.up("md")]: {
              px: headerDesktopPaddingX,
              gridTemplateColumns: isLoggedIn ? "1fr 1fr" : "1fr 3fr 1fr",
            },
          }}
        >
          {!isLoggedIn && (
            <BNBox
              sx={{
                [theme.breakpoints.down("md")]: { display: "none" },
              }}
            ></BNBox>
          )}
          <BNBox
            key="logo-wrapper"
            sx={{
              display: "flex",
              justifyContent: "left",
              maxHeight: "4rem",
              [theme.breakpoints.up("md")]: {
                justifyContent: isLoggedIn ? "left" : "center",
              },
              "@media print": {
                justifyContent: "center",
              },
            }}
          >
            {isApp ? (
              <LevelChangeAnimation
                currentKey={isLogoLevel ? "logo" : levelUpLink.title}
                level={level}
                durationSeconds={0.3}
              >
                {!isLogoLevel ? (
                  <BNButton
                    startIcon={<ArrowBack />}
                    variant="text"
                    textDecoration={true}
                    href={isSpa(levelUpLink.url) ? undefined : levelUpLink.url}
                    to={isSpa(levelUpLink.url) ? levelUpLink.url : undefined}
                    component={isSpa(levelUpLink.url) ? RouterLink : undefined}
                    sx={{
                      maxWidth: "100%",
                      fontWeight: "bold",
                      px: 0,
                      fontSize: "1.3rem",
                      "@media print": {
                        display: "none",
                      },
                    }}
                    data-uit="level-up"
                    noWrap={true}
                  >
                    {levelUpLink.title}
                  </BNButton>
                ) : (
                  <MuiLink
                    component={isSpa && isSpa(logoLink) ? RouterLink : "a"}
                    to={isSpa && isSpa(logoLink) ? logoLink : undefined}
                    href={isSpa && isSpa(logoLink) ? undefined : logoLink}
                    aria-label={altLinkText}
                    sx={sxLogoLink}
                    data-uit={getRouteAction(logoLink)}
                  >
                    <BNLogo dark={appLook && mode === "light"} sx={sxLogo} />
                  </MuiLink>
                )}
              </LevelChangeAnimation>
            ) : (
              <MuiLink
                component={isSpa && isSpa(logoLink) ? RouterLink : "a"}
                to={isSpa && isSpa(logoLink) ? logoLink : undefined}
                href={isSpa && isSpa(logoLink) ? undefined : logoLink}
                aria-label={altLinkText}
                sx={sxLogoLink}
                data-uit={getRouteAction(logoLink)}
              >
                <BNLogo dark={appLook || transparentBg} sx={sxLogo} />
              </MuiLink>
            )}
          </BNBox>
          <BNBox
            sx={{
              display: "flex",
              paddingTop: "0.1rem",
              justifyContent: "right",
              "@media print": {
                display: "none",
              },
            }}
          >
            <BNBox
              sx={[
                {
                  display: "flex",
                  marginRight: -1,
                  color: appLook ? "tertiary.dark" : "common.white",
                },
                appLook && mode === "dark" ? theme.applyStyles("dark", { color: "common.white" }) : {},
              ]}
              data-uit-container={"button-container"}
            >
              {!hideButtons && isLoggedIn && RenderedNotificationsButton}
              {!hideButtons && isLoggedIn && userNavigationLinks && (
                <HeaderButtons links={[userNavigationLinks.help]} iconButtonSx={iconButtonSx} />
              )}
              {!hideButtons && isLoggedIn && (
                <AccountSettingsWrapper
                  settingsLinks={settingsLinks ?? []}
                  userNavigationLinks={userNavigationLinks}
                  openMenuIconButtonSx={iconButtonSx}
                  isSpa={isSpa}
                  strings={strings}
                  accountText={accountText}
                />
              )}
              {isLoggedIn && userNavigationLinks?.logout && (
                <HeaderButton
                  text={userNavigationLinks.logout.title}
                  link={userNavigationLinks.logout.url}
                  buttonId={getRouteAction(userNavigationLinks.logout.url)}
                  isSpa={isSpa(userNavigationLinks.logout.url)}
                  logoSrc={userNavigationLinks.logout.svgIconSrc}
                  navigate={navigation}
                  hidden={{
                    mdDown: hideButtons ? undefined : true,
                  }}
                  iconButtonSx={iconButtonSx}
                />
              )}
              {!isLoggedIn && otherLanguage && (
                <>
                  <Button
                    variant="text"
                    data-uit="switch-language"
                    href={otherLanguage.url}
                    hrefLang={otherLanguage.languageCode}
                    sx={{
                      color: appLook || transparentBg ? "tertiary.main" : "white",
                      textTransform: "uppercase",
                      fontSize: "1.2rem !important",
                      marginRight: 0.5,
                    }}
                  >
                    {otherLanguage.label}
                  </Button>
                </>
              )}

              {!isLoggedIn && !hideButtons && loginLink && (
                <BNButton
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  href={loginLink}
                  data-uit={getRouteAction(loginLink)}
                  sx={{
                    color: appLook || transparentBg ? "tertiary.main" : "white",
                    borderColor: appLook ? "tertiary.main" : "white",
                    whiteSpace: "nowrap",
                    px: 2,
                    fontSize: "1.2rem !important",
                    ["&:hover"]: {
                      borderColor: appLook ? "tertiary.main" : "white",
                    },
                    [theme.breakpoints.up("md")]: {
                      fontSize: "1.6rem !important",
                    },
                    [theme.breakpoints.down("md")]: {
                      marginRight: 1,
                    },
                  }}
                >
                  {strings?.loginLabel || "Login"}
                </BNButton>
              )}
            </BNBox>
          </BNBox>
        </BNBox>
      </Box>
      {children}
    </BNBox>
  );

  interface RightSideUserLinksProps {
    links: NavigationLink[];
    iconButtonSx?: SxProps<Theme>;
  }

  function HeaderButtons({ links, iconButtonSx }: RightSideUserLinksProps) {
    return (
      <>
        {links
          .filter((link) => !!link)
          .map((link: NavigationLink, i: number) => (
            <HeaderButton
              text={link.title}
              key={`header-button-${i}`}
              link={link.url}
              buttonId={getRouteAction(link.url)}
              isSpa={isSpa(link.url)}
              logoSrc={link.svgIconSrc}
              navigate={navigation}
              iconButtonSx={iconButtonSx}
              hidden={{
                mdDown: link.hiddenOnMobile,
                mdUp: link.hiddenOnDesktop,
              }}
            />
          ))}
      </>
    );
  }
};
